import { QbsTable } from 'qbs-react-grid'
import React, { useCallback, useEffect, useState } from 'react'

import { TableColumns } from '../../../common/types'
import Icons from '../../../components/common/icons'
import { calcWindowHeight } from '../../../utilities/calcHeight'
import { handleReturnEmptyMsg } from '../../../utilities/validation'
import { getEngagementReport } from '../api'

const formatDate = (value?: string) => {
  if (!value) return '--'
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const defaultColumnProps = {
  sortable: false,
  resizable: true,
  isVisible: true,
}

const columns: TableColumns[] = [
  {
    title: 'Organisation',
    field: 'organisation',
    colWidth: 220,
    ...defaultColumnProps,
  },
  { title: 'User', field: 'user_name', colWidth: 180, ...defaultColumnProps },
  {
    title: 'User Email',
    field: 'user_email',
    colWidth: 240,
    ...defaultColumnProps,
  },
  {
    title: 'User Status',
    field: 'user_status',
    colWidth: 130,
    ...defaultColumnProps,
  },
  {
    title: 'Last Login',
    field: 'last_login',
    colWidth: 180,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (rowData) => ({
      cell: <>{formatDate(rowData.last_login)}</>,
    }),
  },
  {
    title: 'Application Started',
    field: 'application_started',
    colWidth: 190,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (rowData) => ({
      cell: <>{formatDate(rowData.application_started)}</>,
    }),
  },
  {
    title: 'Application Last Accessed',
    field: 'application_last_accessed',
    colWidth: 210,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (rowData) => ({
      cell: <>{formatDate(rowData.application_last_accessed)}</>,
    }),
  },
  {
    title: 'Application Status',
    field: 'application_status',
    colWidth: 180,
    ...defaultColumnProps,
  },
  {
    title: 'Current Accreditation Level',
    field: 'current_accreditation_level',
    colWidth: 220,
    ...defaultColumnProps,
  },
  {
    title: 'Current Application Level',
    field: 'current_application_level',
    colWidth: 210,
    ...defaultColumnProps,
  },
  {
    title: 'Progress %',
    field: 'progress_percentage',
    colWidth: 130,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (rowData) => ({
      cell: (
        <>
          {rowData.progress_percentage !== null &&
          rowData.progress_percentage !== undefined
            ? `${rowData.progress_percentage}%`
            : '--'}
        </>
      ),
    }),
  },
  {
    title: 'Days Since Last Activity',
    field: 'days_since_last_activity',
    colWidth: 210,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (rowData) => ({
      cell: <>{rowData.days_since_last_activity ?? '--'}</>,
    }),
  },
  {
    title: 'Draft / Submitted',
    field: 'draft_submitted_status',
    colWidth: 170,
    ...defaultColumnProps,
  },
  {
    title: 'Activity Status',
    field: 'activity_status',
    colWidth: 160,
    ...defaultColumnProps,
  },
]

export default function EngagementReport({
  searchParams,
  pageParams,
  onChangePage,
  onChangeRowsPerPage,
  handleSeach,
}: any) {
  const [data, setData] = useState<any>([])
  const tableData = Array.isArray(data) ? data : data?.items ?? []
  const totalRecords = Array.isArray(data) ? data.length : data?.total

  const getData = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { filters, filterProps, ...restSearchParams } = pageParams
    const response = await getEngagementReport({
      ...searchParams,
      ...restSearchParams,
    })
    setData(response)
  }, [pageParams, searchParams])

  useEffect(() => {
    getData()
  }, [getData])

  return (
    <div className="p-4">
      <QbsTable
        data={tableData}
        dataRowKey="id"
        toolbar={true}
        height={calcWindowHeight(335)}
        emptyTitle="No records to display"
        emptySubTitle={handleReturnEmptyMsg(pageParams?.search)}
        columns={columns}
        pagination={true}
        renderSortIcon={(sortType?: 'asc' | 'desc' | undefined) => {
          return sortType === 'asc' ? (
            <Icons name="ascending-icon" />
          ) : sortType === 'desc' ? (
            <Icons name="descending-icon" />
          ) : (
            <Icons name="qbs-sort-icon" />
          )
        }}
        paginationProps={{
          onPagination: onChangePage,
          total: totalRecords,
          currentPage: pageParams?.page,
          rowsPerPage: Number(pageParams?.page_size ?? data?.page_size),
          onRowsPerPage: onChangeRowsPerPage,
          dropOptions: [10, 20, 30, 50, 100],
        }}
        searchValue={pageParams?.search}
        onSearch={handleSeach}
        asyncSearch
        columnToggle
      />
    </div>
  )
}
